/* eslint-disable */
import React, { useEffect, useState, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faSync,
  faCogs,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import axios from "axios";
import moment from "moment";
import { Modal } from "react-bootstrap";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import { hasPermission } from "../../helpers/permission.js";
import "../../components/skeletonRow.css";

const MODULE = "report_subscriber_data";
const MAX_GENERATE_DAYS = 400;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  accept: "application/json",
});

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const formatNumber = (value) =>
  value === null || value === undefined || value === "" ? "----" : Number(value).toLocaleString("en-IN");

const formatDate = (value) => {
  if (!value) return "----";
  const m = moment(value, ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601]);
  return m.isValid() ? m.format("DD-MM-YYYY") : "----";
};

const formatCurrency = (value) =>
  value === null || value === undefined || value === ""
    ? "----"
    : Number(value).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

const PERIOD_COUNT = 10; // current half-year + previous 9 (5 years)
const CUSTOM_PERIOD = "custom";

// Half-year periods run 1 Apr – 30 Sep and 1 Oct – 31 Mar
const getCurrentPeriodStart = () => {
  const today = moment();
  const month = today.month(); // 0 = Jan
  if (month >= 3 && month <= 8) return moment({ year: today.year(), month: 3, day: 1 });
  const year = month >= 9 ? today.year() : today.year() - 1;
  return moment({ year, month: 9, day: 1 });
};

const buildPeriods = () => {
  const currentStart = getCurrentPeriodStart();
  return Array.from({ length: PERIOD_COUNT }).map((_, i) => {
    const start = currentStart.clone().subtract(i * 6, "months");
    const end = start.clone().add(5, "months").endOf("month").startOf("day");
    return {
      key: start.format("YYYY-MM-DD"),
      startDate: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),
      label: `${start.format("MMM YYYY")} – ${end.format("MMM YYYY")}${i === 0 ? " (Current)" : ""}`,
    };
  });
};

const PERIODS = buildPeriods();

const findPeriodKey = (startDate, endDate) =>
  PERIODS.find((p) => p.startDate === startDate && p.endDate === endDate)?.key || CUSTOM_PERIOD;

const SummaryCard = ({ label, value, sub, color, loading }) => (
  <div className="col-6 col-md-4 col-lg-2 mb-2">
    <div
      className="panel panel-default with-rounded-corner with-shadow h-100"
      style={{ padding: "10px 12px", marginBottom: 0, borderLeft: `3px solid ${color}` }}
    >
      <div style={{ fontSize: "0.75rem", color: "#777" }}>{label}</div>
      <div style={{ fontSize: "1.25rem", fontWeight: 600, color }}>
        {loading ? <span className="skeleton-text" style={{ width: "60px", display: "inline-block" }}></span> : value}
      </div>
      {sub && <div style={{ fontSize: "0.7rem", color: "#999" }}>{sub}</div>}
    </div>
  </div>
);

const SubscriberDataReport = () => {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const defaultPeriod = PERIODS[0];

  const [selectedPeriod, setSelectedPeriod] = useState(defaultPeriod.key);
  const [dateRange, setDateRange] = useState([
    moment(defaultPeriod.startDate).toDate(),
    moment(defaultPeriod.endDate).toDate(),
  ]);

  const [filters, setFilters] = useState({
    startDate: defaultPeriod.startDate,
    endDate: defaultPeriod.endDate,
    sortBy: "sdate",
    sortOrder: "DESC",
    page: 1,
    limit: 50,
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    showingRecords: "Showing 0 to 0 of 0 entries",
  });

  // Generate modal
  const [showGenerate, setShowGenerate] = useState(false);
  const [generateRange, setGenerateRange] = useState([null, null]);
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);

  // Client summary table (label / value rows from the summary endpoint)
  const [clientSummary, setClientSummary] = useState([]);
  const [clientSummaryLoading, setClientSummaryLoading] = useState(false);

  const canGenerate = hasPermission(MODULE, "add");

  const fetchData = useCallback(
    async (pageNumber = 1) => {
      try {
        setLoading(true);
        const params = {
          page: pageNumber,
          limit: filters.limit,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
        };

        const response = await axios.get(`${API_URL}report-subscriber-data`, {
          params,
          headers: authHeaders(),
        });

        if (response.data && response.data.success) {
          const res = response.data;
          setData(res.data || []);
          setSummary(res.summary || null);

          const totalRecords = res.total || 0;
          const limit = res.limit || filters.limit;
          const totalPages = res.totalPages || Math.ceil(totalRecords / limit) || 1;
          const startRecord = totalRecords === 0 ? 0 : (pageNumber - 1) * limit + 1;
          const endRecord = Math.min(pageNumber * limit, totalRecords);

          setPagination({
            currentPage: pageNumber,
            totalPages,
            totalRecords,
            showingRecords: `Showing ${startRecord} to ${endRecord} of ${totalRecords} entries`,
          });
        }
      } catch (error) {
        console.error("Error fetching subscriber data report:", error);
        setData([]);
        setSummary(null);
        showToast.error(getErrorMessage(error, "Failed to load subscriber data report"));
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    fetchData(filters.page);
  }, [filters.page, filters.sortBy, filters.sortOrder, filters.startDate, filters.endDate]);

  // Client summary needs both dates; it is independent of paging and sorting
  const fetchClientSummary = useCallback(async () => {
    if (!filters.startDate || !filters.endDate) {
      setClientSummary([]);
      return;
    }
    try {
      setClientSummaryLoading(true);
      const response = await axios.get(`${API_URL}report-subscriber-data/summary`, {
        params: { startDate: filters.startDate, endDate: filters.endDate },
        headers: authHeaders(),
      });
      setClientSummary(response.data?.success ? response.data.data || [] : []);
    } catch (error) {
      console.error("Error fetching client summary:", error);
      setClientSummary([]);
      showToast.error(getErrorMessage(error, "Failed to load client summary"));
    } finally {
      setClientSummaryLoading(false);
    }
  }, [filters.startDate, filters.endDate]);

  useEffect(() => {
    fetchClientSummary();
  }, [fetchClientSummary]);

  const downloadClientSummary = () => {
    if (clientSummary.length === 0) return;
    try {
      const sheetRows = [
        ["Client Summary"],
        [`Period: ${formatDate(filters.startDate)} to ${formatDate(filters.endDate)}`],
        [],
        ["S.No", "Particulars", "Value"],
        ...clientSummary.map((row, idx) => [
          idx + 1,
          row.label,
          row.value === null || row.value === undefined || row.value === "" ? "" : Number(row.value),
        ]),
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
      worksheet["!cols"] = [{ wch: 6 }, { wch: 90 }, { wch: 18 }];

      // Number formats: grouping for counts, rupees for fees (data starts on sheet row 5)
      clientSummary.forEach((row, idx) => {
        const cell = worksheet[XLSX.utils.encode_cell({ r: 4 + idx, c: 2 })];
        if (cell && cell.t === "n") {
          cell.z = row.key === "total_fees_collected" ? '"₹"#,##0.00' : "#,##0";
        }
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Client Summary");
      XLSX.writeFile(workbook, `client-summary-${filters.startDate}_to_${filters.endDate}.xlsx`);
    } catch (error) {
      console.error("Error downloading client summary:", error);
      showToast.error("Failed to download client summary.");
    }
  };

  const refreshAll = (pageNumber) => {
    fetchData(pageNumber);
    fetchClientSummary();
  };

  // ─── Filters ────────────────────────────────────────────────────────────
  const handlePeriodChange = (e) => {
    const key = e.target.value;
    setSelectedPeriod(key);
    if (key === CUSTOM_PERIOD) return; // keep the current range until a custom one is picked

    const period = PERIODS.find((p) => p.key === key);
    if (!period) return;
    setDateRange([moment(period.startDate).toDate(), moment(period.endDate).toDate()]);
    setFilters((prev) => ({ ...prev, startDate: period.startDate, endDate: period.endDate, page: 1 }));
  };

  // Custom range picker
  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setDateRange([start, end]);

    if (start && end) {
      setFilters((prev) => ({
        ...prev,
        startDate: moment(start).format("YYYY-MM-DD"),
        endDate: moment(end).format("YYYY-MM-DD"),
        page: 1,
      }));
    } else if (!start && !end) {
      setFilters((prev) => ({ ...prev, startDate: "", endDate: "", page: 1 }));
    }
  };

  const handleSort = (column) => {
    const isSameCol = filters.sortBy === column;
    const newOrder = isSameCol && filters.sortOrder === "DESC" ? "ASC" : "DESC";
    setFilters((prev) => ({ ...prev, sortBy: column, sortOrder: newOrder, page: 1 }));
  };

  const getSortIcon = (column) => {
    if (filters.sortBy !== column) return faSort;
    return filters.sortOrder === "ASC" ? faSortUp : faSortDown;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters((prev) => ({ ...prev, page: newPage }));
    }
  };

  // ─── Generate / Refresh ─────────────────────────────────────────────────
  const openGenerate = () => {
    // Days after today can't be generated, so cap the end of the range at today
    const [start, end] = dateRange;
    const today = moment().startOf("day");
    const cappedEnd = end && moment(end).isAfter(today) ? today.toDate() : end || null;
    const cappedStart = start && moment(start).isAfter(today) ? null : start || null;
    setGenerateRange([cappedStart, cappedStart ? cappedEnd : null]);
    setGenerateResult(null);
    setShowGenerate(true);
  };

  const handleGenerate = async () => {
    const [start, end] = generateRange;
    if (!start || !end) {
      showToast.error("Please select a start and end date.");
      return;
    }
    const days = moment(end).diff(moment(start), "days") + 1;
    if (days > MAX_GENERATE_DAYS) {
      showToast.error(`Date range cannot exceed ${MAX_GENERATE_DAYS} days.`);
      return;
    }

    try {
      setGenerating(true);
      setGenerateResult(null);
      const startDate = moment(start).format("YYYY-MM-DD");
      const endDate = moment(end).format("YYYY-MM-DD");

      const response = await axios.post(
        `${API_URL}report-subscriber-data/generate`,
        { startDate, endDate },
        { headers: authHeaders() }
      );

      if (response.data && response.data.success) {
        setGenerateResult(response.data);
        showToast.success(response.data.message || "Report generated successfully.");

        // Show the generated range in the table; a range capped at today stays on the current period
        const currentPeriod = PERIODS[0];
        const isCurrentPeriodSoFar =
          startDate === currentPeriod.startDate && moment(endDate).isSameOrBefore(currentPeriod.endDate);
        if (isCurrentPeriodSoFar && selectedPeriod === currentPeriod.key) {
          refreshAll(filters.page);
          return;
        }
        setSelectedPeriod(findPeriodKey(startDate, endDate));
        setDateRange([start, end]);
        if (filters.startDate === startDate && filters.endDate === endDate && filters.page === 1) {
          refreshAll(1);
        } else {
          setFilters((prev) => ({ ...prev, startDate, endDate, page: 1 }));
        }
      }
    } catch (error) {
      console.error("Error generating subscriber data report:", error);
      showToast.error(getErrorMessage(error, "Failed to generate report."));
    } finally {
      setGenerating(false);
    }
  };

  const netChange = summary ? (summary.totalActivated || 0) - (summary.totalExpired || 0) : 0;
  const colCount = 5;

  return (
    <div className="panel-content mt-5">
      <style>{`
        #datepicker-portal .react-datepicker-popper,
        .react-datepicker-popper {
          z-index: 9999 !important;
        }
        .sdr-generate-picker .react-datepicker-wrapper { width: 100%; }
        .sdr-generate-picker input { width: 100%; }
      `}</style>

      {/* ─── Header Controls Bar ────────────────────────────────────────── */}
      <div
        className="panel-heading d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{ padding: "8px 5px", borderBottom: "none" }}
      >
        <div className="d-flex align-items-center flex-wrap gap-2">
          <label htmlFor="sdr-period" style={{ fontSize: "0.8rem", margin: 0 }}>Period</label>
          <select
            id="sdr-period"
            className="form-select form-select-sm"
            style={{ width: "auto", minWidth: "200px" }}
            value={selectedPeriod}
            onChange={handlePeriodChange}
          >
            {PERIODS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
            <option value={CUSTOM_PERIOD}>Custom range</option>
          </select>

          {selectedPeriod === CUSTOM_PERIOD ? (
            <DatePicker
              portalId="datepicker-portal"
              popperProps={{ strategy: "fixed" }}
              selected={dateRange[0]}
              onChange={handleDateChange}
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              selectsRange
              isClearable
              toggleCalendarOnIconClick
              placeholderText="Select date range"
              dateFormat="dd-MM-yyyy"
            />
          ) : (
            <span style={{ fontSize: "0.75rem", color: "#999" }}>
              {formatDate(filters.startDate)} to {formatDate(filters.endDate)}
            </span>
          )}
        </div>

        <div className="d-flex align-items-center flex-wrap gap-1">
          <span style={{ fontSize: "0.8rem" }} className="me-2">
            {loading ? (
              <span className="skeleton-text" style={{ width: "140px", display: "inline-block" }}></span>
            ) : (
              pagination.showingRecords
            )}
          </span>

          <button className="btn btn-default btn-sm" onClick={() => refreshAll(filters.page)} title="Refresh">
            <FontAwesomeIcon icon={faSync} spin={loading} />
          </button>

          {canGenerate && (
            <button className="btn btn-default btn-sm" onClick={openGenerate} title="Generate / refresh report rows">
              <FontAwesomeIcon icon={faCogs} /> Generate
            </button>
          )}

          <button
            className={`btn btn-default btn-sm ${filters.page === 1 ? "d-none" : ""}`}
            title="Previous"
            onClick={() => handlePageChange(filters.page - 1)}
            disabled={filters.page === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button
            className={`btn btn-default btn-sm ${filters.page >= pagination.totalPages ? "d-none" : ""}`}
            title="Next"
            onClick={() => handlePageChange(filters.page + 1)}
            disabled={filters.page >= pagination.totalPages}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>

      {/* ─── Summary Cards ──────────────────────────────────────────────── */}
      <div className="row mx-0 mb-2">
        <SummaryCard
          label="Active Subscribers"
          value={formatNumber(summary?.latestActive)}
          sub={summary?.latestDate ? `as on ${formatDate(summary.latestDate)}` : null}
          color="#2c7be5"
          loading={loading}
        />
        <SummaryCard
          label="Activated"
          value={formatNumber(summary?.totalActivated)}
          sub="in range"
          color="#00a65a"
          loading={loading}
        />
        <SummaryCard
          label="Expired"
          value={formatNumber(summary?.totalExpired)}
          sub="in range"
          color="#dd4b39"
          loading={loading}
        />
        <SummaryCard
          label="Net Change"
          value={summary ? `${netChange > 0 ? "+" : ""}${formatNumber(netChange)}` : "----"}
          sub="activated − expired"
          color={netChange >= 0 ? "#00a65a" : "#dd4b39"}
          loading={loading}
        />
        <SummaryCard
          label="Peak Active"
          value={formatNumber(summary?.peakActive)}
          sub="in range"
          color="#605ca8"
          loading={loading}
        />
        <SummaryCard
          label="Lowest Active"
          value={formatNumber(summary?.lowestActive)}
          sub={summary?.totalDays ? `${summary.totalDays} day(s)` : null}
          color="#f39c12"
          loading={loading}
        />
      </div>

      {/* ─── Client Summary ─────────────────────────────────────────────── */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div
          className="panel-heading d-flex justify-content-between align-items-center"
          style={{ padding: "8px 12px" }}
        >
          <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>Client Summary</span>
          <button
            className="btn btn-default btn-sm"
            onClick={downloadClientSummary}
            disabled={clientSummaryLoading || clientSummary.length === 0}
            title="Download as Excel"
          >
            <FontAwesomeIcon icon={faDownload} /> Download
          </button>
        </div>
        <div className="table-responsive">
          <table className="table table-condensed table-bordered bg-white" style={{ width: "100%", margin: 0 }}>
            <thead>
              <tr>
                <th style={{ width: "50px", textAlign: "center" }}>#</th>
                <th>Particulars</th>
                <th className="text-end" style={{ width: "220px" }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {clientSummaryLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`cs-skeleton-${i}`} className="skeleton-row">
                    <td><div className="skeleton-text" style={{ width: "20px" }}></div></td>
                    <td><div className="skeleton-text" style={{ width: "320px" }}></div></td>
                    <td><div className="skeleton-text" style={{ width: "100px", marginLeft: "auto" }}></div></td>
                  </tr>
                ))
              ) : clientSummary.length > 0 ? (
                clientSummary.map((row, idx) => (
                  <tr key={row.key || idx}>
                    <td style={{ textAlign: "center" }}>{idx + 1}</td>
                    <td>{row.label}</td>
                    <td className="text-end" style={{ fontWeight: 600 }}>
                      {row.key === "total_fees_collected" ? formatCurrency(row.value) : formatNumber(row.value)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center py-3" style={{ color: "#999" }}>
                    {filters.startDate && filters.endDate
                      ? "No summary available for this range"
                      : "Select a date range to see the client summary"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─── Report Table ───────────────────────────────────────────────── */}
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="table-responsive">
          <table className="table table-condensed table-hover bg-white sticky-table" style={{ width: "100%", margin: 0 }}>
            <thead>
              <tr style={{ cursor: "pointer" }}>
                <th style={{ width: "50px", textAlign: "center" }}>#</th>
                <th onClick={() => handleSort("sdate")}>
                  Date <FontAwesomeIcon icon={getSortIcon("sdate")} />
                </th>
                <th className="text-end" onClick={() => handleSort("active_subscribers")}>
                  Active Subscribers <FontAwesomeIcon icon={getSortIcon("active_subscribers")} />
                </th>
                <th className="text-end" onClick={() => handleSort("active_today")}>
                  Activated Today <FontAwesomeIcon icon={getSortIcon("active_today")} />
                </th>
                <th className="text-end" onClick={() => handleSort("expired_today")}>
                  Expired Today <FontAwesomeIcon icon={getSortIcon("expired_today")} />
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="skeleton-row">
                    {Array.from({ length: colCount }).map((__, j) => (
                      <td key={j}><div className="skeleton-text" style={{ width: j === 0 ? "20px" : "80px" }}></div></td>
                    ))}
                  </tr>
                ))
              ) : data.length > 0 ? (
                data.map((item, idx) => (
                  <tr key={item.id || idx}>
                    <td style={{ textAlign: "center" }}>
                      {(pagination.currentPage - 1) * filters.limit + idx + 1}
                    </td>
                    <td>{formatDate(item.sdate)}</td>
                    <td className="text-end">{formatNumber(item.active_subscribers)}</td>
                    <td className="text-end" style={{ color: "#00a65a" }}>{formatNumber(item.active_today)}</td>
                    <td className="text-end" style={{ color: "#dd4b39" }}>{formatNumber(item.expired_today)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={colCount} className="text-center py-4" style={{ color: "#999" }}>
                    No subscriber data found for this range
                    {canGenerate && " — use Generate to build it"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─── Generate Modal ─────────────────────────────────────────────── */}
      <Modal show={showGenerate} onHide={() => !generating && setShowGenerate(false)} centered>
        <Modal.Header closeButton={!generating}>
          <Modal.Title style={{ fontSize: "1rem" }}>Generate / Refresh Subscriber Data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: "0.8rem", color: "#777" }}>
            Creates or updates one row per day for the selected range (max {MAX_GENERATE_DAYS} days).
            Long ranges can take a few seconds.
          </p>
          <div className="sdr-generate-picker mb-2">
            <label style={{ fontSize: "0.8rem" }}>Date range</label>
            <DatePicker
              portalId="datepicker-portal"
              popperProps={{ strategy: "fixed" }}
              selected={generateRange[0]}
              onChange={(dates) => setGenerateRange(dates)}
              startDate={generateRange[0]}
              endDate={generateRange[1]}
              selectsRange
              isClearable
              maxDate={new Date()}
              disabled={generating}
              placeholderText="Select date range"
              dateFormat="dd-MM-yyyy"
              className="form-control"
            />
            {generateRange[0] && generateRange[1] && (
              <div style={{ fontSize: "0.75rem", color: "#999", marginTop: 4 }}>
                {moment(generateRange[1]).diff(moment(generateRange[0]), "days") + 1} day(s) selected
              </div>
            )}
          </div>

          {generating && (
            <div className="text-center py-3">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Generating report, please wait...
            </div>
          )}

          {generateResult && !generating && (
            <div className="alert alert-success mb-0" style={{ fontSize: "0.85rem" }}>
              <div>{generateResult.message}</div>
              <div className="mt-1">
                <strong>{generateResult.totalDays}</strong> day(s) ·{" "}
                <strong>{generateResult.created}</strong> created ·{" "}
                <strong>{generateResult.updated}</strong> updated
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-default btn-sm" onClick={() => setShowGenerate(false)} disabled={generating}>
            Close
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleGenerate} disabled={generating}>
            {generating ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Generating...
              </>
            ) : (
              "Generate"
            )}
          </button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default SubscriberDataReport;
